import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';

export interface AnalysisFile {
  analysis_mode?: 'intraday' | 'swing' | 'unknown' | string;
  market_type?: 'stock' | 'future' | 'index' | 'unknown' | string;
  symbol?: string | null;
  timeframe?: string | null;
  point_value_eur?: number | null;
  max_loss_eur?: number;
  minimum_rr?: number;
  screenshot_quality?: AnalysisScreenshotQuality;
  overall_assessment?: AnalysisOverallAssessment;
  technical_levels?: AnalysisTechnicalLevels;
  trade_variants?: AnalysisVariant[];
  final_recommendation?: AnalysisFinalRecommendation;
  questions_if_needed?: string[];
  disclaimer?: string;
  datumanalyse?: string;
  analyseZeitpunkt?: string;
  analyseDatum?: string;
  instrument?: string;
  extrahierteDaten?: Record<string, boolean | number | string | null>;
  bildValidierung?: Record<string, unknown>;
  footprintValidierung?: Record<string, unknown>;
  bildQualitaet?: Record<string, boolean | number | string | null>;
  analyseStatus?: string;
  marktstruktur?: Record<string, unknown>;
  emaBewertung?: Record<string, unknown>;
  volumeProfileBewertung?: Record<string, unknown>;
  agentenFazit?: Record<string, unknown>;
  bias?: {
    richtung?: string;
    staerke?: number;
    daily?: string;
    h4?: string;
    h1?: string;
    beschreibung?: string;
  };
  basisAnalyse?: {
    marktstruktur?: string;
    volumeProfile?: string;
    liquiditaet?: string;
    footprint?: string;
    wichtigeZonen?: number[];
  };
  checkliste?: Record<string, boolean | number | string | null>;
  emaAnalyse?: Record<string, boolean | number | string | null>;
  szenarioKandidaten?: AnalysisScenarioCandidate[];
  szenarien?: AnalysisVariant[];
  varianten?: AnalysisVariant[];
  validierung?: Record<string, boolean | number | string | null>;
  datenluecken?: string | string[] | Record<string, string>;
}

export interface AnalysisScreenshotQuality {
  is_sufficient?: boolean;
  missing_information?: string[];
  readability_notes?: string[];
}

export interface AnalysisOverallAssessment {
  primary_bias?: 'bullish' | 'bearish' | 'neutral' | 'unclear' | string;
  confidence_percent?: number;
  summary?: string;
}

export interface AnalysisFinalRecommendation {
  recommended_variant?: string | null;
  action?: 'enter_now' | 'wait_for_trigger' | 'watchlist' | 'no_trade' | 'request_more_information' | string;
  reason?: string;
}

export interface AnalysisTechnicalLevels {
  support_zones?: AnalysisPriceZone[];
  resistance_zones?: AnalysisPriceZone[];
  value_zones?: AnalysisValueZone[];
  nearest_upside_target_zone?: AnalysisPriceZone | null;
  nearest_downside_target_zone?: AnalysisPriceZone | null;
}

export interface AnalysisPriceZone {
  timeframe?: string;
  zone_low?: number | null;
  zone_high?: number | null;
  strength?: string;
  relevance?: string;
  basis?: string;
  reasoning?: string;
}

export interface AnalysisValueZone extends AnalysisPriceZone {
  source?: string;
  zone_type?: string;
  function?: string;
}

export interface AnalysisScenarioCandidate {
  name?: string;
  trend?: string;
  szenario?: string;
  gesamtScore?: number;
  auswahl?: string;
}

export interface AnalysisVariant {
  variant_name?: string;
  direction?: 'long' | 'short' | 'none' | string;
  probability_percent?: number;
  status?: 'active' | 'valid_if_triggered' | 'watchlist' | 'no_trade' | 'invalid' | string;
  setup_type?: string;
  entry?: number | null;
  stop_loss?: number | null;
  take_profit?: number | null;
  risk_reward_ratio?: number | null;
  max_loss_eur?: number;
  expected_profit_eur?: number | null;
  position_size?: {
    quantity?: number | null;
    unit?: string;
    calculation?: string;
  };
  reasoning?: string[];
  validity_conditions?: string[];
  invalidation?: string[];
  risks?: string[];
  rang?: number;
  name?: string;
  szenario?: string;
  trend?: string;
  richtung?: string;
  einstieg?: number;
  einstiegZone?: string;
  stopLos?: number;
  stopLoss?: string;
  ziel?: number;
  ziel1?: string;
  erweitertesZiel?: number | string;
  bereich?: string;
  wahrscheinlichkeit?: number;
  begruendung?: string | string[];
  setup?: string;
  reaktionszone?: number;
  invalidierung?: number | string;
  bestaetigung?: string;
  volumeProfileBewertung?: string | Record<string, unknown>;
  footprintBewertung?: string | Record<string, unknown>;
  bestaetigungExakt?: Record<string, unknown>;
  statusDerVariante?: string;
  emaBewertung?: Record<string, unknown>;
  scoreDetails?: Record<string, number>;
  trigger?: Array<{ wenn?: string; und?: string; dann?: number }>;
}

@Component({
  selector: 'app-analysis-dialog',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './analysis-dialog.component.html',
  styleUrls: ['./analysis-dialog.component.scss']
})
export class AnalysisDialogComponent {
  @Input() analysisData: AnalysisFile | null = null;
  @Input() showApply = false;
  @Output() close = new EventEmitter<void>();
  @Output() applyVariant = new EventEmitter<AnalysisVariant>();

  analysisDate(): string {
    return this.analysisData?.datumanalyse || this.analysisData?.analyseZeitpunkt || this.analysisData?.analyseDatum || '-';
  }

  analysisInstrument(): string {
    return this.analysisData?.symbol
      || this.analysisData?.instrument
      || this.analysisData?.extrahierteDaten?.['symbol']?.toString()
      || '-';
  }

  displayVariants(): AnalysisVariant[] {
    return this.analysisData?.trade_variants?.length
      ? this.analysisData.trade_variants
      : this.analysisData?.varianten?.length
      ? this.analysisData.varianten
      : this.analysisData?.szenarien ?? [];
  }

  isSchemaOutput(): boolean {
    return !!this.analysisData?.trade_variants;
  }

  headerDescription(): string {
    const data = this.analysisData;
    if (!data) return '';
    return [
      this.modeLabel(data.analysis_mode),
      this.marketLabel(data.market_type),
      data.timeframe,
    ].filter(Boolean).join(' | ');
  }

  riskDescription(): string {
    const data = this.analysisData;
    if (!data) return '';
    return `Max. Risiko: ${data.max_loss_eur ?? '-'} EUR | Mindest-CRV: 1:${data.minimum_rr ?? '-'}`;
  }

  actionLabel(action?: string): string {
    const labels: Record<string, string> = {
      enter_now: 'Aktiver Trade moeglich',
      wait_for_trigger: 'Auf Trigger warten',
      watchlist: 'Beobachten',
      no_trade: 'Kein Trade',
      request_more_information: 'Mehr Informationen noetig',
    };
    return action ? labels[action] ?? this.analysisLabel(action) : '-';
  }

  biasLabel(value?: string): string {
    const labels: Record<string, string> = {
      bullish: 'Bullish',
      bearish: 'Bearish',
      neutral: 'Neutral',
      unclear: 'Unklar',
    };
    return value ? labels[value] ?? this.analysisLabel(value) : '-';
  }

  statusLabel(status?: string): string {
    const labels: Record<string, string> = {
      active: 'Aktiv moeglich',
      valid_if_triggered: 'Gueltig bei Trigger',
      watchlist: 'Beobachten',
      no_trade: 'Kein Trade',
      invalid: 'Ungueltig',
    };
    return status ? labels[status] ?? this.analysisLabel(status) : '-';
  }

  directionLabel(direction?: string): string {
    const value = direction?.toLowerCase();
    if (value === 'long') return 'Long';
    if (value === 'short') return 'Short';
    return 'Kein Trade';
  }

  setupTypeLabel(value?: string): string {
    return value ? this.analysisLabel(value) : '-';
  }

  validityTitle(variant: AnalysisVariant): string {
    if (variant.direction === 'none' || variant.status === 'no_trade') {
      return 'Warum kein Trade?';
    }
    return 'Der Trade ist nur valide, wenn:';
  }

  variantStatusClass(variant: AnalysisVariant): string {
    return `analysis-card--${variant.status ?? 'legacy'}`;
  }

  isRecommendedVariant(variant: AnalysisVariant): boolean {
    const recommended = this.analysisData?.final_recommendation?.recommended_variant;
    return !!recommended && this.variantTitle(variant) === recommended;
  }

  screenshotWarnings(): string[] {
    const quality = this.analysisData?.screenshot_quality;
    return [
      ...(quality?.is_sufficient === false ? ['Screenshot ist nicht ausreichend lesbar. Trade-Karten sind nicht als aktive Empfehlung zu verwenden.'] : []),
      ...(quality?.missing_information ?? []),
      ...(quality?.readability_notes ?? []),
    ];
  }

  schemaValidationWarnings(): string[] {
    if (!this.isSchemaOutput() || !this.analysisData) return [];
    const data = this.analysisData;
    const variants = data.trade_variants ?? [];
    const warnings: string[] = [];

    if (variants.length !== 3) {
      warnings.push(`Es wurden ${variants.length} statt 3 Varianten geliefert.`);
    }
    const probabilitySum = variants.reduce((sum, variant) => sum + Number(variant.probability_percent ?? 0), 0);
    if (variants.length && probabilitySum !== 100) {
      warnings.push(`Die Wahrscheinlichkeiten ergeben ${probabilitySum}% statt 100%.`);
    }
    if (data.screenshot_quality?.is_sufficient === false) {
      warnings.push('Die Bildqualitaet ist laut Analyse nicht ausreichend.');
    }

    const minimumRr = Number(data.minimum_rr ?? 3);
    const recommended = variants.find((variant) => this.isRecommendedVariant(variant));
    if (recommended && this.isTradeVariant(recommended) && Number(recommended.risk_reward_ratio ?? 0) < minimumRr) {
      warnings.push(`Die empfohlene Variante hat ein CRV unter 1:${minimumRr}.`);
    }

    variants.forEach((variant) => {
      if (Number(variant.max_loss_eur ?? 0) > Number(data.max_loss_eur ?? 50)) {
        warnings.push(`${this.variantTitle(variant)} überschreitet den maximalen Verlust.`);
      }
      if (this.isTradeVariant(variant) && (variant.entry == null || variant.stop_loss == null || variant.take_profit == null)) {
        warnings.push(`${this.variantTitle(variant)} ist als Trade markiert, hat aber keinen vollständigen Trade-Plan.`);
      }
    });

    return warnings;
  }

  hasSchemaWarning(): boolean {
    return this.schemaValidationWarnings().length > 0;
  }

  analysisDirection(variant: AnalysisVariant): 'Long' | 'Short' {
    const direction = variant.direction || variant.trend || variant.richtung || variant.setup || '';
    return direction.toLowerCase() === 'short' ? 'Short' : 'Long';
  }

  analysisCandidateDirection(candidate: AnalysisScenarioCandidate): 'Long' | 'Short' {
    return candidate.trend?.toLowerCase() === 'short' ? 'Short' : 'Long';
  }

  analysisScoreEntries(scoreDetails?: Record<string, number>): Array<{ label: string; value: number }> {
    return Object.entries(scoreDetails ?? {}).map(([key, value]) => ({
      label: this.analysisLabel(key),
      value,
    }));
  }

  analysisValidationEntries(): Array<{ label: string; value: string }> {
    return this.analysisObjectEntries(this.analysisData?.validierung);
  }

  objectEntries(value?: Record<string, unknown>): Array<{ label: string; value: string }> {
    return this.analysisObjectEntries(value);
  }

  analysisChecklistEntries(): Array<{ label: string; value: string }> {
    return this.analysisObjectEntries(this.analysisData?.checkliste);
  }

  analysisEmaEntries(): Array<{ label: string; value: string }> {
    return this.analysisObjectEntries(this.analysisData?.emaAnalyse ?? this.analysisData?.emaBewertung);
  }

  extractedDataEntries(): Array<{ label: string; value: string }> {
    return this.analysisObjectEntries(this.analysisData?.extrahierteDaten);
  }

  imageQualityEntries(): Array<{ label: string; value: string }> {
    return this.analysisObjectEntries(this.analysisData?.bildQualitaet);
  }

  footprintValidationEntries(): Array<{ label: string; value: string }> {
    return this.analysisObjectEntries(this.analysisData?.footprintValidierung);
  }

  marketStructureEntries(): Array<{ label: string; value: string }> {
    return this.analysisObjectEntries(this.analysisData?.marktstruktur);
  }

  volumeProfileEntries(): Array<{ label: string; value: string }> {
    return this.analysisObjectEntries(this.analysisData?.volumeProfileBewertung);
  }

  agentConclusionEntries(): Array<{ label: string; value: string }> {
    return this.analysisObjectEntries(this.analysisData?.agentenFazit);
  }

  analysisEmaRatingEntries(variant: AnalysisVariant): Array<{ label: string; value: string }> {
    return this.analysisObjectEntries(variant.emaBewertung);
  }

  analysisExactConfirmationEntries(variant: AnalysisVariant): Array<{ label: string; value: string }> {
    return this.analysisObjectEntries(variant.bestaetigungExakt);
  }

  analysisDataGapText(): string {
    const value = this.analysisData?.datenluecken;
    if (!value) return '';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') {
      return Object.entries(value)
        .map(([key, item]) => `${this.analysisLabel(key)}: ${item}`)
        .join(', ');
    }
    return value;
  }

  begruendungText(variant: AnalysisVariant): string {
    if (Array.isArray(variant.reasoning)) {
      return variant.reasoning.join(' ');
    }
    if (Array.isArray(variant.begruendung)) {
      return variant.begruendung.join(' ');
    }
    return variant.begruendung ?? '';
  }

  stopValue(variant: AnalysisVariant): number | string | undefined {
    return variant.stop_loss ?? variant.stopLos ?? variant.stopLoss ?? variant.invalidierung;
  }

  targetValue(variant: AnalysisVariant): number | string | undefined {
    return variant.take_profit ?? variant.ziel ?? variant.ziel1;
  }

  extendedTargetValue(variant: AnalysisVariant): number | string | undefined {
    return variant.erweitertesZiel ?? variant['ziel2' as keyof AnalysisVariant] as string | undefined;
  }

  variantTitle(variant: AnalysisVariant): string {
    return variant.variant_name || variant.name || variant.szenario || `Szenario ${variant.rang ?? ''}`.trim();
  }

  exactConditionText(variant: AnalysisVariant): string {
    return variant.bestaetigung || this.analysisValue(variant.bestaetigungExakt);
  }

  isTradeVariant(variant: AnalysisVariant): boolean {
    return variant.direction === 'long' || variant.direction === 'short';
  }

  hasTradePlan(variant: AnalysisVariant): boolean {
    return this.isTradeVariant(variant) && variant.entry != null && variant.stop_loss != null && variant.take_profit != null;
  }

  listItems(value?: string[] | string): string[] {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }

  zoneTitle(zone: AnalysisPriceZone | AnalysisValueZone): string {
    const valueType = 'zone_type' in zone && zone.zone_type ? `${zone.zone_type} ` : '';
    const low = zone.zone_low ?? '-';
    const high = zone.zone_high ?? '-';
    return `${valueType}${low} - ${high}`;
  }

  zoneMeta(zone: AnalysisPriceZone | AnalysisValueZone): string {
    const valueZone = zone as AnalysisValueZone;
    return [
      zone.timeframe,
      zone.strength,
      zone.relevance,
      valueZone.function,
      valueZone.source,
      zone.basis,
    ].filter(Boolean).join(' | ');
  }

  modeLabel(value?: string): string {
    const labels: Record<string, string> = {
      intraday: 'Intraday',
      swing: 'Swingtrade',
      unknown: 'Unbekannt',
    };
    return value ? labels[value] ?? this.analysisLabel(value) : '';
  }

  marketLabel(value?: string): string {
    const labels: Record<string, string> = {
      stock: 'Aktie',
      future: 'Future',
      index: 'Index',
      unknown: 'Unbekannt',
    };
    return value ? labels[value] ?? this.analysisLabel(value) : '';
  }

  private analysisObjectEntries(value?: Record<string, unknown>): Array<{ label: string; value: string }> {
    return Object.entries(value ?? {}).map(([key, item]) => ({
      label: this.analysisLabel(key),
      value: this.analysisValue(item),
    }));
  }

  private analysisValue(value: unknown): string {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'Ja' : 'Nein';
    if (typeof value === 'number') return Number.isFinite(value) ? value.toString() : '-';
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) return value.map((item) => this.analysisValue(item)).join(', ');
    if (typeof value === 'object') {
      return Object.entries(value as Record<string, unknown>)
        .map(([key, item]) => `${this.analysisLabel(key)}: ${this.analysisValue(item)}`)
        .join(' | ');
    }
    return String(value);
  }

  analysisLabel(key: string): string {
    return key
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/_/g, ' ')
      .replace(/^./, (char) => char.toUpperCase());
  }
}
